import { FC, useEffect, useState } from 'react';
import {
    Card,
    CardActions,
    CardContent,
    CardHeader,
    IconButton,
    Skeleton,
} from '@mui/material';
import { GroupWork } from '@mui/icons-material';
import { format, formatISO, subYears } from 'date-fns';
import {
    useLazyGetDeviceConsumptionQuery,
    useLazyGetDeviceReadingsQuery,
} from 'store/services';
import { useAuth } from 'hooks';
import { Device, DeviceConsumptionResult, DeviceReadingsResult } from 'types';
import { convertReadingsDictionaryToList } from 'helpers/general';
import DeviceInformationUsageGraph from './components/DeviceInformationUsageGraph/DeviceInformationUsageGraph';
import styles from './DeviceInformationGraphCard.module.scss';

interface DeviceInformationGraphCardProps {
    device: Device;
}

const DeviceInformationGraphCard: FC<DeviceInformationGraphCardProps> = ({
    device,
}) => {
    const { user } = useAuth();
    const [queryDeviceReadings] = useLazyGetDeviceReadingsQuery();
    const [queryDeviceConsumption] = useLazyGetDeviceConsumptionQuery();
    const [readings, setReadings] = useState<DeviceReadingsResult | null>(null);
    const [deviceInformationLoading, setDeviceInformationLoading] =
        useState(true);
    const [tooltipGroupingEnabled, setTooltipGroupingEnabled] = useState(false);
    const [consumption, setConsumption] =
        useState<DeviceConsumptionResult | null>(null);

    const onTooltipGroupingClick = () => {
        setTooltipGroupingEnabled(!tooltipGroupingEnabled);
    };

    useEffect(() => {
        const queryDeviceInformation = async () => {
            const endTime = subYears(new Date(), 1);

            await queryDeviceReadings({
                deviceId: device.deviceId,
                start: format(endTime, 'yyyy-MM-dd'),
            }).then(({ data }) => {
                if (data) {
                    const output = convertReadingsDictionaryToList(data);
                    setReadings(output);
                }
            });

            await queryDeviceConsumption({
                deviceId: device.deviceId,
                start: formatISO(endTime),
                end: formatISO(new Date()),
            }).then(({ data }) => {
                if (data) setConsumption(data);
            });
        };

        queryDeviceInformation().then(() => setDeviceInformationLoading(false));
    }, [device.deviceId, queryDeviceConsumption, queryDeviceReadings]);

    return (
        <div
            data-testid="DeviceInformationGraphCard"
            className={styles.DeviceInformationGraphCard}
        >
            <Card>
                <CardHeader title="Usage Graph" />
                <CardContent className={styles.graph}>
                    {!deviceInformationLoading ? (
                        <DeviceInformationUsageGraph
                            readings={readings}
                            consumption={consumption}
                            admin={
                                user?.protected?.supplierId?.toString() === '0'
                            }
                            tooltipGroupingEnabled={tooltipGroupingEnabled}
                        />
                    ) : (
                        <div>
                            <Skeleton
                                variant="rectangular"
                                width={600}
                                height={400}
                            />
                        </div>
                    )}
                </CardContent>
                <CardActions className={styles.actionArea}>
                    <IconButton
                        onClick={onTooltipGroupingClick}
                        className={
                            tooltipGroupingEnabled
                                ? styles.tooltipGroupingEnabled
                                : styles.tooltipGroupingDisabled
                        }
                    >
                        <GroupWork />
                    </IconButton>
                </CardActions>
            </Card>
        </div>
    );
};

export default DeviceInformationGraphCard;
